import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import '@blocknote/shadcn/style.css';
import './editor.less';
import { BlockNoteSchema, defaultBlockSpecs } from '@blocknote/core';
import { owuploadCustomRequest } from '../../thunks/file_thunks';
import html2pdf from 'html2pdf.js';
import { Button, message, Input } from 'antd';
import { inlineImagesAsBase64, renderAttachmentsAsLinks } from '../../utils/ImageUtils';
import { withUserType } from '../withUserType';
import { DownloadOutlined, RedoOutlined, UndoOutlined } from '@ant-design/icons';
import { debounce } from '../../utils/PerformanceUtils';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { withCurrentUser } from '../../hooks/withCurrentUser';
import { EditorProps } from '../../types/documentTypes';
import DateTimeHover from '../date_time_component/DateTime';
import DocumentPermissionsDropdown from './DocumentPermissionsDropdown';
import { withCompanyConfig } from '../../hooks/withCompanyConfig';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { stringArrToList, userRoleTextFormatter } from '../../utils/DataFormatterUtils';

const schema = BlockNoteSchema.create({
	blockSpecs: {
		...defaultBlockSpecs,
	},
});

const BlockNoteEditor: React.FC<EditorProps> = ({
	userType,
	document,
	fullDocument,
	updateDocument,
	currentUser,
	editable = true,
	showToolbar = true,
	companyConfig,
	timeZoneId,
}) => {
	const editorRef = useRef(null);
	const [isSaving, setIsSaving] = useState(false);
	const [lastSaved, setLastSaved] = useState<Date | null>(
		nullSafeGetOrElse('updatedAt', fullDocument, null)
	);
	const previousContentRef = useRef<string | null>(null);
	const [title, setTitle] = useState(nullSafeGetOrElse('title', document, ''));

	const isNewDocument = useMemo(() => {
		const blocks = nullSafeGet('blocks', document);
		return !blocks || (Array.isArray(blocks) && blocks.length === 0) || blocks === '';
	}, [document]);

	// this is a hack to prevent the editor from autofocusing on the first block if it is a file/image
	const initialContent = useMemo(() => {
		const blocks = nullSafeGet('blocks', document);
		if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
			return blocks || '';
		}

		const firstBlock = blocks[0];
		const firstBlockType = nullSafeGet('type', firstBlock);
		if (firstBlockType === 'file' || firstBlockType === 'image') {
			// empty paragraph block
			const emptyParagraph = {
				id: `empty-para-${Date.now()}`,
				type: 'paragraph',
				props: {
					textColor: 'default',
					textAlignment: 'left',
					backgroundColor: 'default',
				},
				content: [],
				children: [],
			};
			return [emptyParagraph, ...blocks];
		}

		return blocks;
	}, [document]);

	const editor = useCreateBlockNote({
		schema,
		initialContent: initialContent,
		uploadFile: async (file: File) => {
			return new Promise((resolve, reject) => {
				const customRequest = {
					file,
					onError: (err: any) => reject(err),
					onSuccess: ({ data }: any) => {
						resolve(getProtectedImageUriGeneric(data.fileId, data.fileName));
					},
					onProgress: () => {},
					userType,
				};
				owuploadCustomRequest(customRequest);
			});
		},
	});

	const exportPDF = async () => {
		const element = editorRef.current;
		if (!element) return;

		// trigger print styles
		element.classList.add('pdf-exporting');

		try {
			await inlineImagesAsBase64(element);
			await renderAttachmentsAsLinks(element);

			await html2pdf()
				.from(element)
				.set({
					margin: 10,
					filename: title || 'document.pdf',
					html2canvas: { scale: 2, useCORS: true },
					jsPDF: { unit: 'mm', format: 'a4' },
				})
				.save();
		} finally {
			// Remove class
			element.classList.remove('pdf-exporting');
		}
	};
	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(window.location.href);
			message.success('Link copied to clipboard!');
		} catch {
			message.error('Failed to copy link');
		}
	};
	const saveContent = (titleToSave?: string) => {
		setIsSaving(true);
		const blocks = editor.document;

		const contentStr = JSON.stringify(blocks);
		const originalTitle = nullSafeGetOrElse('title', document, '');

		// Check if either content or title has changed
		if (previousContentRef.current === contentStr && originalTitle === titleToSave) {
			setIsSaving(false);
			return;
		}

		const documentData = {
			...fullDocument,
			document: {
				blocks,
				title: titleToSave,
			},
			updatedAt: new Date(),
		};
		updateDocument(documentData)
			.then(() => {
				previousContentRef.current = contentStr;
				setLastSaved(new Date());
			})
			.catch((err) => {
				console.error('Error saving content:', err);
				message.error('Failed to save content');
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	const debouncedSave = useRef(debounce(saveContent, 2000)).current;

	useEffect(() => {
		if (!editor) return;

		const unsubscribe = editor.onEditorContentChange(() => {
			debouncedSave(title);
		});

		return () => {
			if (typeof unsubscribe === 'function') {
				unsubscribe();
			}
		};
	}, [editor, debouncedSave, title]);

	return (
		<div className="min-h-screen bg-white pb-8">
			<div className="">
				{showToolbar ? (
					<div
						className="flex flex-row flex-wrap items-center justify-between gap-y-2 px-6 py-4"
						style={{ borderBottom: '1px solid #E5E5E5' }}
					>
						<div className="flex items-center space-x-4 text-sm text-gray-500">
							<Button disabled={!editable} onClick={() => editor.undo()} icon={<UndoOutlined />}>
								Undo
							</Button>
							<Button disabled={!editable} onClick={() => editor.redo()} icon={<RedoOutlined />}>
								Redo
							</Button>
						</div>
						<div className=" flex items-center justify-end gap-4">
							{isSaving ? (
								<div className="bodyregular">Saving...</div>
							) : lastSaved ? (
								<div className="bodyregular">
									Edited{' '}
									<DateTimeHover
										timestamp={lastSaved}
										dateFormat={'hh:mm A z'}
										showDate
										timeZone={timeZoneId}
									/>
								</div>
							) : null}
							<div className="flex cursor-pointer flex-row items-center gap-4">
								<div className=" flex items-center gap-2">
									<ContactAvatar
										size={16}
										hidePopover={true}
										contact={currentUser}
										userType={userType}
									/>
									<div className="flex flex-row items-center gap-1">
										<span className="bodyregular">
											{stringArrToList((currentUser.roles || []).map(userRoleTextFormatter))}
										</span>
										<span className="bodyregular text-character-secondary">
											Can {editable ? 'Edit' : 'View'}
										</span>
									</div>
								</div>
							</div>
							<DocumentPermissionsDropdown
								fullDocument={fullDocument}
								userType={userType}
								companyConfig={companyConfig}
								updateDocument={updateDocument}
								currentUser={currentUser}
								editable={editable}
								handleCopyLink={handleCopyLink}
							/>
							<Button icon={<DownloadOutlined />} onClick={exportPDF} />
						</div>
					</div>
				) : null}

				<div
					className="mx-auto max-w-4xl bg-white"
					style={{
						minHeight: '80vh',
						padding: '24px 0',
						fontSize: '14px',
						lineHeight: 1.6,
					}}
					ref={editorRef}
				>
					<div className="bn-shadcn bn-editor mb-4">
						<Input.TextArea
							autoSize
							rows={1}
							disabled={!editable}
							value={title}
							onChange={(e) => {
								setTitle(e.target.value);
								debouncedSave(e.target.value);
							}}
							placeholder="New Document"
							bordered={false}
							autoFocus={isNewDocument}
							className="document-title-input mb-4 placeholder-gray-400 focus:outline-none"
							style={{
								fontSize: 46,
								lineHeight: '1.25',
								color: '#000000',
							}}
						/>
						<div className="document-title-print">{title || ''}</div>
					</div>
					<BlockNoteView editable={editable} theme="light" editor={editor}></BlockNoteView>
				</div>
			</div>
		</div>
	);
};

export default withUserType(withCurrentUser(withCompanyConfig(BlockNoteEditor)));
